import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, Tag, Gift, AlertCircle } from 'lucide-react';

import ActionButton from '../../components/ActionButton'; 
import RadioOption from '../../components/RadioOption'; 

// --- 3. Logika pro nákup za konkrétní částku v Kč (Obrácená matematika) ---
export const getCreditsForMoney = (money) => {
  let C = money;
  let d = 0;
  
  // Používáme přepočítané hranice v Kč, abychom zjistili správnou úroveň slevy
  if (money >= 90000) { d = 0.10; C = money / 0.90; }
  else if (money >= 69000) { d = 0.08; C = money / 0.92; }
  else if (money >= 47500) { d = 0.05; C = money / 0.95; }
  else if (money >= 24250) { d = 0.03; C = money / 0.97; }

  return {
    credits: Math.ceil(C), // Zaokrouhlíme nahoru na celé kredity
    discountPercent: d * 100
  };
};



const CostCalculatorPage = ({ isAuthenticated = false }) => {
  const navigate = useNavigate();

  // Stav pro kalkulačku analýzy
  const [questionType, setQuestionType] = useState(null);
  const [numResponses, setNumResponses] = useState(1000);
  const [calcResult, setCalcResult] = useState(null);
  const [error, setError] = useState(null); // Přidán stav pro chybu

  const handleCalculate = (e) => {
    e.preventDefault();

    // Přirozenější zobrazení chyby namísto alert()
    if (!questionType) {
      setError("Vyberte prosím, jaký typ otázky chcete analyzovat.");
      return;
    }

    let baseCost = questionType === 'brands' ? 450 : 1890;
    let costPerResponse = questionType === 'brands' ? 0.22 : 0.3;

    // U analýzy se počítá čistá cena v kreditech (1 kredit = 1 Kč)
    let subtotal = baseCost + numResponses * costPerResponse;

    setCalcResult({
      creditsNeeded: subtotal.toFixed(0)
    });
  };

  return (
    <div className="flex justify-center min-h-full p-4 pt-8 pb-16 bg-gray-50">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl p-8 md:p-12">
        
        {/* Hlavička */}
        <div className="w-full text-center mb-10">
          <Calculator className="w-12 h-12 mx-auto text-blue-600 mb-4" />
          <h2 className="text-3xl font-bold text-gray-800">Ceník a výpočet analýzy</h2>
          <p className="text-gray-500 mt-2">Přečtěte si, jak tvoříme ceny, a spočítejte si analýzu na míru.</p>
        </div>

        {/* 1. Cenová politika - Vysvětlení */}
        <div className="mb-8 p-6 bg-gray-50 border border-gray-200 rounded-xl">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-5 h-5 text-gray-700" />
            <h3 className="text-xl font-bold text-gray-800">Jak počítáme cenu?</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Cena se skládá ze <strong>základního poplatku</strong> (nastavení a zpracování modelu) a <strong>poplatku za každou odpověď</strong>. Platíte kredity (1 kredit = 1 Kč).
          </p>
          <ul className="space-y-4">
            <li className="flex flex-col sm:flex-row sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
              <span className="font-semibold text-gray-800 sm:w-1/2">1. Vyjmenování značek</span>
              <span className="text-blue-600 sm:w-1/2 mt-1 sm:mt-0 font-medium">Základ 450 kreditů + 0,22 / odpověď</span>
            </li>
            <li className="flex flex-col sm:flex-row sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
              <span className="font-semibold text-gray-800 sm:w-1/2">2. Vlastnosti, výhody, asociace</span>
              <span className="text-blue-600 sm:w-1/2 mt-1 sm:mt-0 font-medium">Základ 1890 kreditů + 0,30 / odpověď</span>
            </li>
          </ul>
        </div>

        {/* 2. Množstevní Bonusy */}
        <div className="mb-10 p-6 bg-blue-50 border border-blue-100 rounded-xl">
          <div className="flex items-center gap-2 mb-4">
            <Gift className="w-6 h-6 text-blue-700" />
            <h3 className="text-xl font-bold text-blue-800">Bonusy za větší nákup</h3>
          </div>
          <p className="text-blue-700 mb-5">
            Získejte extra kredity navíc ke svému dobití. Uživatel platí plnou cenu, ale na účet se mu připíše bonus.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-center">
            <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100">
              <div className="text-sm text-gray-500 mb-1">Nákup nad</div>
              <div className="font-bold text-lg text-gray-800">25 000 Kč</div>
              <div className="text-lg font-extrabold text-blue-600 mt-2">+3 % kreditů</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100">
              <div className="text-sm text-gray-500 mb-1">Nákup nad</div>
              <div className="font-bold text-lg text-gray-800">50 000 Kč</div>
              <div className="text-lg font-extrabold text-blue-600 mt-2">+5 % kreditů</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100">
              <div className="text-sm text-gray-500 mb-1">Nákup nad</div>
              <div className="font-bold text-lg text-gray-800">75 000 Kč</div>
              <div className="text-lg font-extrabold text-blue-600 mt-2">+8 % kreditů</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100">
              <div className="text-sm text-gray-500 mb-1">Nákup nad</div>
              <div className="font-bold text-lg text-gray-800">100 000 Kč</div>
              <div className="text-lg font-extrabold text-blue-600 mt-2">+10 % kreditů</div>
            </div>
          </div>
        </div>

        <hr className="my-10 border-gray-200" />

        {/* 3. Kalkulačka konkrétní analýzy */}
        <form onSubmit={handleCalculate} className="flex flex-col items-center">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 w-full text-left">Kalkulačka ceny analýzy</h3>

          <div className="w-full space-y-4 mb-6">
            <h4 className="font-semibold text-lg text-gray-800">1. Typ otázky</h4>
            <RadioOption
              id="brands"
              name="analysisType"
              label="Vyjmenujte značky"
              description="Například: Které značky oblečení si vybavíte?"
              checked={questionType === 'brands'}
              onChange={(val) => {
                setQuestionType(val);
                setError(null); // Skryje chybu, když uživatel vybere možnost
              }}
            />
            <RadioOption
              id="advantages"
              name="analysisType"
              label="Vyjmenujte vlastnosti, výhody, asociace"
              description="Například: Proč byste si koupil oblečení značky NoName?"
              checked={questionType === 'advantages'}
              onChange={(val) => {
                setQuestionType(val);
                setError(null); // Skryje chybu, když uživatel vybere možnost
              }}
            />
          </div>

          <div className="w-full space-y-2 mb-8">
            <label htmlFor="numResponses" className="font-semibold text-lg text-gray-800">
              2. Počet odpovědí
            </label>
            <input
              id="numResponses"
              type="number"
              min="0"
              value={numResponses}
              onChange={(e) => setNumResponses(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Např. 1000"
            />
          </div>

          {/* Zobrazení chyby */}
          {error && (
            <div className="w-full mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-3 animate-fade-in">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">{error}</span>
            </div>
          )}

          <div className="w-full">
            <ActionButton type="submit" className="w-full">
              Spočítat cenu analýzy
            </ActionButton>
          </div>

          {calcResult !== null && !error && (
            <div className="w-full mt-8 p-6 bg-gray-50 border border-gray-200 rounded-lg text-center animate-fade-in">
              <p className="text-lg text-gray-700">Tato analýza bude vyžadovat:</p>
              <p className="text-4xl font-bold text-blue-600 mt-2 mb-6">{calcResult.creditsNeeded} kreditů</p>
              
              {isAuthenticated && (
                //<CheckoutButton credits={Number(calcResult.creditsNeeded)} from_create={false} isTemporary={false} /> 
                <button 
                  type="button"
                  onClick={() => navigate('/payments/buy', { 
                    state: { recommendedCredits: calcResult.creditsNeeded } 
                  })}
                  className="px-8 py-3 bg-blue-600 text-white font-bold rounded-lg shadow hover:bg-blue-700 transition-colors"
                >
                  Přejít k dobití kreditů
                </button>
              )}
            </div>
          )}
        </form>

      </div>
    </div>
  );
};

export default CostCalculatorPage;